import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { ICallAgent, IUpsertCallAgentRequest } from "../models/CallQueue";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

// Admin, Call Centre. Reading the settings makes the API create any missing CallCentre: keys with
// their defaults, so the values themselves are then edited through the normal /setting endpoints.
export default function useCallCentreSettings(componentName = "useCallCentreSettings") {
  const { get, put, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const provisioned = useQuery({
    queryKey: ["callCentreSettings"],
    queryFn: () => get<DefaultResponseWithData<Record<string, string>>>("/callcentresettings"),
    staleTime: 1000 * 60 * 5,
  });

  const agents = useQuery({
    queryKey: ["callCentreSettings", "agents"],
    queryFn: () => get<DefaultResponseWithData<ICallAgent[]>>("/callcentresettings/agents"),
  });

  const upsertAgent = useMutation(
    ({ userId, body }: { userId: string; body: IUpsertCallAgentRequest }) =>
      put<IUpsertCallAgentRequest, DefaultResponseWithData<ICallAgent>>(`/callcentresettings/agents/${userId}`, body),
    { onSettled: () => queryClient.invalidateQueries(["callCentreSettings", "agents"]) }
  );

  const removeAgent = useMutation(
    (userId: string) => del<DefaultResponse>(`/callcentresettings/agents/${userId}`),
    { onSettled: () => queryClient.invalidateQueries(["callCentreSettings", "agents"]) }
  );

  return { provisioned, agents, upsertAgent, removeAgent };
}
